/*
 * Minuteur de reservation du panier.
 * Calque sur le comportement de la reference :
 *  - sessionStorage, on stocke la DATE DE DEPART (pas l'echeance)
 *  - le minuteur ne repart pas a zero quand le panier change
 *  - re-tick au retour d'onglet (visibilitychange)
 *  - a 0 : affiche 0:00, pas de rechargement de page
 *
 * Durcissements par rapport a la reference (corrige le bug du --:--) :
 *  - [data-ref="time"] est relu A CHAQUE TICK (getter paresseux).
 *    Sans ca, quand la classe est deja definie, l'element est upgrade sur sa
 *    balise ouvrante, AVANT que ses enfants soient parses : querySelector rend
 *    null dans connectedCallback et l'affichage reste bloque sur --:--.
 *  - pas de sortie anticipee sur data-cart-empty (le masquage est fait en CSS).
 *  - si le depart memorise est deja expire et qu'on ne vide pas le panier,
 *    on repart d'un cycle neuf au lieu d'afficher 0:00 pour toute la session.
 */
const STORAGE_KEY = 'cart-reservation-timer';

class CartReservationTimer extends HTMLElement {
  constructor() {
    super();
    this.tick = this.tick.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
  }

  connectedCallback() {
    this.durationMinutes = parseFloat(this.dataset.durationMinutes) || 5;
    this.durationMs = this.durationMinutes * 60 * 1000;
    this.clearOnExpire = this.dataset.clearCartOnExpire === 'true';
    this.expired = false;
    this.startTime = this.getStartTime();

    this.tick();
    this.interval = window.setInterval(this.tick, 1000);
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  handleVisibility() {
    if (!document.hidden) this.tick();
  }

  /* Relu a chaque appel : l'element peut etre upgrade avant ses enfants. */
  get timeEl() {
    return this.querySelector('[data-ref="time"]');
  }

  read() {
    try {
      return parseInt(window.sessionStorage.getItem(STORAGE_KEY), 10);
    } catch (error) {
      return NaN;
    }
  }

  write(value) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(value));
    } catch (error) {
      /* navigation privee : on tourne en memoire */
    }
  }

  getStartTime() {
    const saved = this.read();
    const valid = Number.isFinite(saved) && saved > 0;
    if (valid) {
      const elapsed = Date.now() - saved;
      if (elapsed < this.durationMs) return saved;
      /* Deja expire : on ne repart que si le panier n'est pas vide a l'expiration. */
      if (this.clearOnExpire) return saved;
    }
    const now = Date.now();
    this.write(now);
    return now;
  }

  tick() {
    const el = this.timeEl;
    if (!el) return;

    const remaining = this.durationMs - (Date.now() - this.startTime);
    if (remaining <= 0) {
      this.expire(el);
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const next = minutes + ':' + String(seconds).padStart(2, '0');
    if (el.textContent !== next) el.textContent = next;
  }

  expire(el) {
    window.clearInterval(this.interval);
    if (el) el.textContent = '0:00';
    if (this.expired) return;
    this.expired = true;

    if (!this.clearOnExpire || CartReservationTimer.clearing) return;
    CartReservationTimer.clearing = true;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(() => {
        try {
          window.sessionStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          /* ignore */
        }
        document.querySelectorAll('[data-ref="cart-count"]').forEach((node) => {
          node.textContent = '0';
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(() => {
        CartReservationTimer.clearing = false;
      });
  }
}

CartReservationTimer.clearing = false;

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
